/**
 * Discover V1 — Editorial.
 *
 * Magazine-style layout: a rotating "featured" carousel (editor's picks) up
 * top, themed shelves (Trending, Editor's Picks) below, and a compact
 * "Recently active" list at the bottom. When searching, collapses into a
 * clean result grid showing model icons and created/updated dates.
 *
 * The sections, their order, looks and counts come from the caller's layout
 * document (#2291, `useDiscoverLayout`); with none stored it is the built-in
 * default, today's page. Saved searches are sections of their own, saved
 * from the results ("☆ Save search") and listed in the Filters panel.
 *
 * Each section loads on its own (see `useDiscoverData`) and shows its own
 * placeholder, empty and error states, so one slow or failing list never
 * holds up the rest of the page. A list no drawn section needs is never
 * asked for (#2292). A saved search, the scheduled tasks and the models
 * shared with the viewer (#2303) each ask for their own list, once near.
 *
 * "Customize" opens the Customize sheet (#2292, loaded on first use): the
 * page behind it is its live preview.
 */
import {lazy, Suspense, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faXmark, faCompass, faFireFlameCurved, faStar, faClock,
    faWrench, faArrowRightLong, faChevronLeft, faChevronRight, faHashtag,
    faHeart, faDiagramProject, faSliders, faLink, faTableLayout,
} from '@fortawesome/pro-solid-svg-icons';
import toast from 'react-hot-toast';
import AuroraBackground from '@/components/layout/AuroraBackground';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
    useDiscoverData,
    labelOf,
    descOf,
    nameLabelOf,
    descLabelOf,
    tagsOf,
    updatedAt,
    type DiscoverItem,
    type SectionStatus,
    type UseDiscoverData,
} from './useDiscoverData';
import {useModelPreview} from './useModelPreview';
import {disposePreviewRenderer} from './modelPreviewService';
import {getModel} from '@/api/models';
import {getDnField} from '@/utils/label';
import {AppFooter, CLASSIFICATION_BANNER_HEIGHT} from '@brew/ui';
import { useAppInfo } from '@/app/useAppInfo';
import ApiImage from '@/components/common/ApiImage';
import MarkedText from '@/components/common/marking/MarkedText';
import {
    DEFAULT_FEATURED_SOURCE,
    favoriteKind,
    favoriteRoute,
    featuredItems,
    filterFavorites,
    type FavoriteFilter,
    type FeaturedSource,
} from './favorites';
import FavoriteKindChips from './FavoriteKindChips';
import { useAuth } from '@/auth/useAuth';
import {useDiscoverSearch} from './search/useDiscoverSearch';
import SearchBox from './search/SearchBox';
import SearchChips from './search/SearchChips';
import FiltersPanel from './search/FiltersPanel';
import SearchHelp from './search/SearchHelp';
import {searchLink} from './search/searchUrl';
import {SORT_LABELS, chipText, serializeQuery, type Field, type SearchQuery} from './search/searchSyntax';
import {CoverGrid, ItemsView, SectionFrame, SectionHead, SectionMessage, SectionSkeleton} from './DiscoverCards';
import {HERO_PALETTES, hashStr} from './discoverArt';
import {favoriteCover} from './covers';
import type {DiscoverSection, DiscoverSectionType, SavedSearch} from '@/api/discoverPreferences';
import {useDiscoverLayout} from './saved/useDiscoverLayout';
import {outcomeMessages, type WriteOutcome} from './saved/discoverLayoutStore';
import {
    addSearch,
    countOf,
    NO_LISTS,
    drawnSections,
    featuredSourceOf,
    filterBoxModesOf,
    filterBoxOf,
    fromModelQuery,
    listsFor,
    missingTypes,
    moveSection,
    newSearchId,
    removeSection,
    searchOf,
    setFilterBox,
    summarizeQuery,
    toSavedQuery,
    updateSearchQuery,
    variantOf,
} from './saved/discoverLayout';
import SavedSearchSection from './saved/SavedSearchSection';
import SavedSearchList from './saved/SavedSearchList';
import SaveSearchDialog, {type SaveSearchValues} from './saved/SaveSearchDialog';
import ScheduledTasksSection from './sections/ScheduledTasksSection';
import SharedWithMeSection from './sections/SharedWithMeSection';
import './saved/customizeSheet.css';

// Its own chunk: dnd-kit and the sheet load the first time someone customizes.
const CustomizeSheet = lazy(() => import('./saved/CustomizeSheet'));

const styles = `
  @keyframes edit-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .edit-search {
    width: 100%;
    background: rgba(10, 16, 30, 0.72);
    border: 1px solid rgb(var(--brew-blue-light) / 0.14);
    border-radius: 14px;
    padding: 13px 16px 13px 44px;
    color: #d4eaf5; font-size: 14.5px; outline: none;
    transition: all 0.25s ease;
  }
  .edit-search:focus {
    border-color: rgb(var(--brew-blue-light) / 0.36);
    box-shadow: 0 0 26px rgb(var(--brew-blue-light) / 0.1);
  }
  .edit-section-head {
    display: flex; align-items: baseline; justify-content: space-between;
    margin-bottom: 14px;
  }
  .edit-section-title {
    color: #d4eaf5; font-size: 18px; font-weight: 700; letter-spacing: -0.01em;
    display: inline-flex; align-items: center; gap: 10px;
  }
  .edit-section-kicker {
    font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
    color: rgb(var(--brew-blue-light) / 0.85); font-weight: 700;
  }
  .edit-section-link {
    font-size: 12px; font-weight: 600; color: rgb(var(--brew-blue-light) / 0.7);
    text-decoration: none; cursor: pointer;
    transition: color 0.15s ease;
  }
  .edit-section-link:hover { color: #3cb4e5; }
  .edit-hero {
    position: relative;
    border-radius: 22px;
    overflow: hidden;
    border: 1px solid rgb(var(--brew-blue-light) / 0.18);
    cursor: pointer;
    transition: transform 0.4s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.4s ease;
    animation: edit-in 0.4s ease both;
    box-shadow: 0 18px 40px rgba(0,0,0,0.35);
  }
  .edit-hero:hover { transform: translateY(-3px); box-shadow: 0 24px 56px rgb(var(--brew-blue-light) / 0.18), 0 18px 40px rgba(0,0,0,0.45); }

  .edit-shelf {
    display: flex; gap: 14px; overflow-x: auto; overflow-y: hidden;
    scroll-behavior: smooth; padding: 4px 2px 14px;
    scrollbar-width: thin; scrollbar-color: rgb(var(--brew-blue-light) / 0.25) transparent;
  }
  .edit-shelf::-webkit-scrollbar { height: 6px; }
  .edit-shelf::-webkit-scrollbar-thumb {
    background: rgb(var(--brew-blue-light) / 0.2); border-radius: 999px;
  }
  .edit-shelf-card {
    flex: 0 0 280px;
    background: rgba(10, 16, 30, 0.7);
    backdrop-filter: blur(14px);
    border: 1px solid rgb(var(--brew-blue-light) / 0.1);
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.2,0.8,0.2,1);
    animation: edit-in 0.35s ease both;
  }
  .edit-shelf-card:hover {
    transform: translateY(-3px);
    border-color: rgb(var(--brew-blue-light) / 0.3);
    box-shadow: 0 14px 36px rgba(0,0,0,0.35), 0 6px 20px rgb(var(--brew-blue-light) / 0.1);
  }
  .edit-picks-grid {
    display: grid; gap: 14px;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }
  .edit-pick {
    background: rgba(10, 16, 30, 0.6);
    backdrop-filter: blur(12px);
    border: 1px solid rgb(var(--brew-blue-light) / 0.08);
    border-radius: 14px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.25s ease;
    animation: edit-in 0.3s ease both;
  }
  .edit-pick:hover {
    border-color: rgb(var(--brew-blue-light) / 0.28);
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0,0,0,0.3);
  }
  .edit-row {
    display: grid;
    grid-template-columns: 36px 1fr auto auto;
    gap: 14px; align-items: center;
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(10, 16, 30, 0.5);
    border: 1px solid rgb(var(--brew-blue-light) / 0.06);
    cursor: pointer;
    transition: all 0.2s ease;
    animation: edit-in 0.25s ease both;
  }
  .edit-row:hover {
    background: rgba(10, 16, 30, 0.72);
    border-color: rgb(var(--brew-blue-light) / 0.18);
  }
  /* Keyboard-only focus ring for the button-role cards (#2414). focus-visible
     keeps mouse users' clicks ring-free; the plain focus rule sets outline none
     to make that explicit across browsers. */
  .edit-shelf-card:focus,
  .edit-pick:focus,
  .edit-row:focus { outline: none; }
  .edit-shelf-card:focus-visible,
  .edit-pick:focus-visible,
  .edit-row:focus-visible {
    outline: 2px solid #3cb4e5;
    outline-offset: 2px;
  }
  .edit-tag {
    display: inline-block;
    padding: 2px 8px; border-radius: 6px;
    font-size: 10.5px; font-weight: 600;
    background: rgb(var(--brew-blue-light) / 0.08);
    border: 1px solid rgb(var(--brew-blue-light) / 0.14);
    color: rgb(var(--brew-blue-light) / 0.9);
  }
  .edit-iconpill {
    display: inline-flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg, rgb(var(--brew-blue-light) / 0.2), rgb(var(--brew-blue) / 0.1));
    border: 1px solid rgb(var(--brew-blue-light) / 0.18);
    color: #3cb4e5; font-size: 14px; font-weight: 700; shrink: 0;
  }
  .edit-shelf-btn {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(10, 16, 30, 0.72);
    border: 1px solid rgb(var(--brew-blue-light) / 0.14);
    color: rgba(180, 220, 240, 0.85);
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: all 0.2s ease;
  }
  .edit-shelf-btn:hover { background: rgb(var(--brew-blue-light) / 0.14); color: #3cb4e5; border-color: rgb(var(--brew-blue-light) / 0.32); }
  .edit-shelf-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  @keyframes edit-fade { from { opacity: 0; } to { opacity: 1; } }
  .edit-hero-bg { position: absolute; inset: 0; animation: edit-fade 0.6s ease both; }
  .edit-hero-content {
    position: relative; z-index: 2;
    display: flex; flex-direction: column; justify-content: flex-end;
    min-height: 380px; max-width: 620px;
    padding: 40px 40px 36px;
    animation: edit-fade 0.5s ease both;
  }
  .edit-hero-badge {
    align-self: flex-start;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 11px; border-radius: 999px;
    background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.22);
    color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    backdrop-filter: blur(8px);
  }
  .edit-hero-ctrls {
    position: absolute; top: 16px; right: 16px; z-index: 3;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 5px; border-radius: 999px;
    background: rgba(8,14,28,0.55); border: 1px solid rgba(255,255,255,0.12);
    backdrop-filter: blur(10px);
  }
  .edit-hero-dots { display: inline-flex; align-items: center; gap: 6px; padding: 0 4px; }
  .edit-hero-dot { width: 7px; height: 7px; border-radius: 999px; border: none; background: rgba(255,255,255,0.4); cursor: pointer; padding: 0; transition: all 0.25s ease; }
  .edit-hero-dot.active { width: 22px; background: #fff; }
  .edit-hero-dot:hover { background: rgba(255,255,255,0.75); }
  .edit-date { font-size: 11px; color: rgb(var(--aurora-text-muted) / 0.6); display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
  .edit-new-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 18px; border-radius: 12px;
    font-size: 13px; font-weight: 700; white-space: nowrap;
    background: linear-gradient(135deg, #007cba, #3cb4e5);
    border: none; color: #fff; cursor: pointer;
    box-shadow: 0 4px 16px rgb(var(--brew-blue-light) / 0.3);
    transition: all 0.25s ease;
  }
  .edit-new-btn:hover { box-shadow: 0 6px 24px rgb(var(--brew-blue-light) / 0.45); transform: translateY(-1px); }

  /* Per-section loading placeholders, in the shape of the cards they stand for. */
  @keyframes edit-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
  .edit-skel {
    background: rgb(var(--brew-blue-light) / 0.09);
    border-radius: 6px;
    animation: edit-pulse 1.4s ease-in-out infinite;
  }
  .is-skel { pointer-events: none; cursor: default; }
  @media (prefers-reduced-motion: reduce) { .edit-skel { animation: none; } }
  .edit-section-msg {
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
    padding: 18px 16px; border-radius: 14px;
    background: rgba(10, 16, 30, 0.5);
    border: 1px dashed rgb(var(--brew-blue-light) / 0.14);
    color: rgb(var(--aurora-text-muted) / 0.7); font-size: 13px;
  }
  button.edit-section-link { background: none; border: none; padding: 0; }
  .edit-results-head {
    display: flex; align-items: center; justify-content: space-between; gap: 8px 14px; flex-wrap: wrap;
    margin-bottom: 14px;
  }
  .edit-empty {
    padding: 48px 16px; text-align: center;
    color: rgb(var(--aurora-text-muted) / 0.65); font-size: 14px;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
  }
  /* Section heads with actions, and the saved searches' pieces (#2291). */
  .edit-section-head { gap: 12px; }
  .edit-section-desc {
    margin: 4px 0 0; max-width: 640px;
    color: rgb(var(--aurora-text-muted) / 0.72); font-size: 13px; line-height: 1.5;
    overflow-wrap: anywhere;
  }
  .edit-section-kickrow { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  /* The actions share the kicker's line without making it taller. */
  .edit-section-actions { display: inline-flex; align-items: center; gap: 10px; flex-shrink: 0; margin: -8px 0; }
  /* A shelf's scroll buttons sit at the head's bottom right: its last line keeps clear of them. */
  .is-shelf .edit-section-head > div > :last-child { padding-right: 80px; }
  .edit-menu { position: relative; }
  .edit-menu-list {
    position: absolute; right: 0; top: calc(100% + 6px); z-index: 40;
    min-width: 200px; padding: 5px;
    display: flex; flex-direction: column;
    background: rgba(9, 14, 28, 0.97);
    border: 1px solid rgb(var(--brew-blue-light) / 0.2);
    border-radius: 12px;
    box-shadow: 0 18px 44px rgba(0,0,0,0.5);
  }
  .edit-menu-item {
    text-align: left; padding: 8px 10px; border-radius: 8px;
    background: none; border: 0; color: #d4eaf5; font-size: 13px; cursor: pointer;
  }
  .edit-menu-item:hover:not(:disabled), .edit-menu-item:focus-visible { background: rgb(var(--brew-blue-light) / 0.14); outline: none; }
  .edit-menu-item:disabled { opacity: 0.4; cursor: not-allowed; }
  .edit-section-msg.is-compact { padding: 10px 14px; gap: 8px; }
  .edit-menu-sep { height: 1px; margin: 4px 6px; border: 0; background: rgb(var(--brew-blue-light) / 0.14); }
  .edit-menu-item[role="menuitemradio"] { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .edit-menu-check { font-size: 11px; color: #3cb4e5; }
  /* A saved search's filter box (#2302): beside "See all", below the title on a phone. */
  .edit-section-head.has-filter > div {
    display: grid; grid-template-columns: minmax(0, 1fr) auto auto; column-gap: 10px;
    grid-template-areas: "kicker filter actions" "title title title" "desc desc desc";
  }
  .has-filter .edit-section-kickrow { display: contents; }
  .has-filter .edit-section-kicker { grid-area: kicker; align-self: center; min-width: 0; }
  .has-filter .edit-section-actions { grid-area: actions; align-self: center; }
  .has-filter .edit-section-title { grid-area: title; justify-self: start; }
  .has-filter .edit-section-desc { grid-area: desc; }
  .has-filter .edit-filter { grid-area: filter; align-self: center; margin: -8px 0; }
  .is-shelf .edit-section-head.has-filter > div > .edit-filter { padding-right: 0; }
  .is-shelf .edit-section-head.has-filter > div > :nth-last-child(2) { padding-right: 80px; }
  .edit-filter-field { position: relative; display: flex; align-items: center; }
  .edit-filter-input {
    width: 190px; height: 30px; padding: 0 28px 0 28px; border-radius: 8px;
    background: rgba(10, 16, 30, 0.72);
    border: 1px solid rgb(var(--brew-blue-light) / 0.14);
    color: #d4eaf5; font-size: 12.5px; outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .edit-filter-input::placeholder { color: rgb(var(--aurora-text-muted) / 0.55); }
  .edit-filter-input::-webkit-search-cancel-button { -webkit-appearance: none; display: none; }
  .edit-filter-input:focus { border-color: rgb(var(--brew-blue-light) / 0.4); box-shadow: 0 0 16px rgb(var(--brew-blue-light) / 0.1); }
  .edit-filter-icon { position: absolute; left: 10px; font-size: 11px; color: rgb(var(--brew-blue-light) / 0.6); pointer-events: none; }
  .edit-filter-clear {
    position: absolute; right: 4px; width: 22px; height: 22px; border-radius: 6px;
    display: inline-flex; align-items: center; justify-content: center;
    background: none; border: 0; color: rgba(180, 220, 240, 0.7); cursor: pointer;
  }
  .edit-filter-clear:hover, .edit-filter-clear:focus-visible { background: rgb(var(--brew-blue-light) / 0.14); color: #3cb4e5; outline: none; }
  .is-filtering > :not(.edit-section-head) { opacity: 0.55; transition: opacity 0.15s ease; }
  @media (max-width: 480px) {
    .edit-section-head.has-filter > div {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas: "kicker actions" "title title" "desc desc" "filter filter";
    }
    .has-filter .edit-filter { margin: 10px 0 0; }
    .is-shelf .edit-section-head.has-filter > div > .edit-filter { padding-right: 80px; }
    .is-shelf .edit-section-head.has-filter > div > :nth-last-child(2) { padding-right: 0; }
    .edit-filter-field { width: 100%; }
    /* 16px: a phone does not zoom into it */
    .edit-filter-input { width: 100%; height: 36px; font-size: 16px; }
  }
`;

function HeroSkeleton() {
    return (
        <div className="edit-hero is-skel" aria-hidden="true"
             style={{minHeight: 380, marginBottom: 36, background: 'rgba(8,14,28,1)'}}>
            <div className="edit-hero-content">
                <div className="edit-skel" style={{width: 96, height: 22, borderRadius: 999}}/>
                <div className="edit-skel" style={{width: '70%', height: 34, marginTop: 14}}/>
                <div className="edit-skel" style={{width: '30%', height: 13, marginTop: 10}}/>
                <div className="edit-skel" style={{width: '90%', height: 14, marginTop: 18}}/>
                <div className="edit-skel" style={{width: '80%', height: 14, marginTop: 8}}/>
                <div className="edit-skel" style={{width: 160, height: 40, marginTop: 22, borderRadius: 11}}/>
            </div>
        </div>
    );
}

function HeroLogo({id, src, version}: Readonly<{ id: string; src?: string; version?: string | number }>) {
    const [err, setErr] = useState(false);
    const showImg = !!src && !err;
    const {ref, dataUrl} = useModelPreview(id, version, !showImg);
    if (showImg) {
        return (
            <div style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '40%',
                minWidth: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 36,
                pointerEvents: 'none'
            }}>
                <ApiImage
                    src={src}
                    alt=""
                    loading="lazy"
                    onError={() => setErr(true)}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '60%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.55))'
                    }}
                />
            </div>
        );
    }
    // No avatar: render the canvas snapshot (when ready) on the hero's right half.
    return (
        <div ref={ref} style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '46%',
            minWidth: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 28,
            pointerEvents: 'none'
        }}>
            {dataUrl && (
                // Panel styling lives on the <img> so it sizes to the model's natural
                // aspect ratio (no letterbox gap); the transparent snapshot shows the
                // dark panel through it and the padding hugs the model evenly.
                <img
                    src={dataUrl}
                    alt=""
                    style={{
                        display: 'block',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: '82%',
                        objectFit: 'contain',
                        borderRadius: 14,
                        padding: 14,
                        background: 'radial-gradient(120% 120% at 50% 40%, rgba(6,10,20,0.34), rgba(3,6,14,0.55))',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                        filter: 'contrast(1.12) brightness(1.1)',
                        animation: 'edit-fade 0.4s ease both'
                    }}
                />
            )}
        </div>
    );
}

/**
 * Featured carousel — pages through several "featured" models (editor's picks).
 * Background is a per-model gradient with a strong left/bottom scrim so the
 * text stays readable; the avatar (if any) sits as a contained logo on the
 * right. Auto-advances every 7s, pauses on hover, dots + prev/next to navigate.
 */
export function FeaturedCarousel({items, navigate}: Readonly<{ items: DiscoverItem[]; navigate: (p: string) => void }>) {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const count = items.length;

    // Keep the index valid if the list shrinks: back to the first model,
    // during render, so no frame shows the last model on the way there.
    // `idx > 0` keeps this from re-queuing the same update when it is
    // already 0 (an empty list): a render-phase update does not bail out.
    if (idx > 0 && idx > count - 1) setIdx(0);

    // Auto-advance (paused while hovered).
    useEffect(() => {
        if (paused || count <= 1) return;
        const t = setTimeout(() => setIdx(i => (i + 1) % count), 7000);
        return () => clearTimeout(t);
    }, [idx, paused, count]);

    if (count === 0) return null;
    const safe = Math.min(idx, count - 1);
    const item = items[safe];
    const label = labelOf(item);
    const desc = descOf(item);
    const tags = tagsOf(item);
    const by = item.metadata?.createdBy;
    const byDisplay = by ? (getDnField(by, 'CN') || by) : '';
    const seed = hashStr(item._id + label);
    const [c1, c2, c3] = HERO_PALETTES[seed % HERO_PALETTES.length];
    const angle = seed % 360;

    return (
        <div
            className="edit-hero"
            style={{minHeight: 380, marginBottom: 36, background: 'rgba(8,14,28,1)'}}
            role="none"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onClick={() => navigate(`/build/${item._id}`)}
        >
            <div key={item._id} className="edit-hero-bg">
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(${angle}deg, ${c1}, ${c2}, ${c3})`
                }}/>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(55% 70% at 22% 28%, ${c3}aa, transparent 60%), radial-gradient(50% 70% at 82% 72%, ${c1}aa, transparent 60%)`,
                    mixBlendMode: 'screen'
                }}/>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1.3px)',
                    backgroundSize: '20px 20px',
                    mixBlendMode: 'overlay'
                }}/>
                {/* Readability scrims — strong on the left (text), softer on the right (logo). */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(90deg, rgba(5,10,22,0.95) 0%, rgba(5,10,22,0.82) 40%, rgba(5,10,22,0.42) 72%, rgba(5,10,22,0.2) 100%)'
                }}/>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(5,10,22,0.05) 45%, rgba(5,10,22,0.5) 100%)'
                }}/>
                <HeroLogo key={'logo' + item._id} id={item._id} src={item.$icon} version={updatedAt(item)}/>
            </div>

            <div key={'c' + item._id} className="edit-hero-content">
        <span className="edit-hero-badge">
          <FontAwesomeIcon icon={faStar} className="text-[10px]"/>
          Featured
        </span>
                <h2 style={{
                    color: '#fff',
                    fontSize: 34,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    marginTop: 14,
                    marginBottom: 0,
                    textShadow: '0 2px 24px rgba(0,0,0,0.55)'
                }}>
                    <MarkedText label={nameLabelOf(item)} fallback="Untitled"/>
                </h2>
                {byDisplay && (
                    <p style={{
                        color: 'rgba(255,255,255,0.82)',
                        fontSize: 13,
                        marginTop: 8,
                        marginBottom: 0,
                        textShadow: '0 1px 6px rgba(0,0,0,0.4)'
                    }}>
                        by {byDisplay}
                    </p>
                )}
                {desc && (
                    <p style={{
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: 15,
                        marginTop: 14,
                        marginBottom: 0,
                        lineHeight: 1.55,
                        maxWidth: 560,
                        textShadow: '0 1px 12px rgba(0,0,0,0.5)',
                        display: '-webkit-box',
                        WebkitLineClamp: 8,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        <MarkedText label={descLabelOf(item)}/>
                    </p>
                )}
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3.5">
                        {tags.slice(0, 5).map(t => (
                            <span key={t} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '3px 10px',
                                borderRadius: 999,
                                background: 'rgba(255,255,255,0.16)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                color: '#fff',
                                fontSize: 11.5,
                                fontWeight: 600,
                                backdropFilter: 'blur(8px)'
                            }}>
                <FontAwesomeIcon icon={faHashtag} className="text-[9px] opacity-80"/>
                                {t}
              </span>
                        ))}
                    </div>
                )}
                <div className="flex gap-2 mt-[22px]">
                    <button
                        onClick={e => {
                            e.stopPropagation();
                            navigate(`/build/${item._id}`);
                        }}
                        style={{
                            padding: '10px 22px',
                            borderRadius: 11,
                            background: '#fff',
                            color: '#0a4d72',
                            border: 'none',
                            fontSize: 13.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
                        }}
                    >
                        <FontAwesomeIcon icon={faWrench} className="text-[11px]"/>
                        Open in Build
                        <FontAwesomeIcon icon={faArrowRightLong} className="text-[11px]"/>
                    </button>
                </div>
            </div>

            {count > 1 && (
                <div className="edit-hero-ctrls" role="none" onClick={e => e.stopPropagation()}>
                    <button className="edit-shelf-btn" style={{width: 28, height: 28}}
                            onClick={() => setIdx((safe - 1 + count) % count)} title="Previous">
                        <FontAwesomeIcon icon={faChevronLeft} className="text-[11px]"/>
                    </button>
                    <div className="edit-hero-dots">
                        {items.map((it, i) => (
                            <button
                                key={it._id}
                                onClick={() => setIdx(i)}
                                className={'edit-hero-dot' + (i === safe ? ' active' : '')}
                                title={labelOf(it)}
                                aria-label={`Featured ${i + 1} of ${count}`}
                            />
                        ))}
                    </div>
                    <button className="edit-shelf-btn" style={{width: 28, height: 28}}
                            onClick={() => setIdx((safe + 1) % count)} title="Next">
                        <FontAwesomeIcon icon={faChevronRight} className="text-[11px]"/>
                    </button>
                </div>
            )}
        </div>
    );
}

/** A featured section's models, or why there are none yet: from the picks, the favorites, or both. */
function featuredStatusOf(source: FeaturedSource, picks: SectionStatus, favorites: SectionStatus): SectionStatus {
    if (source === 'picks') return picks;
    if (source === 'favorites') return favorites;
    if (picks === 'loading' || favorites === 'loading') return 'loading';
    return picks === 'error' && favorites === 'error' ? 'error' : 'ready';
}

/**
 * The favorites section: the caller's models, documents and functionals,
 * newest first, of the kinds its settings name, filtered by the kind chips on
 * the client (#2293). Its own component, so each favorites section has its
 * own chip.
 */
function FavoritesSection({section, favorites, navigate, className}: Readonly<{
    section: DiscoverSection;
    favorites: UseDiscoverData['favorites'];
    navigate: (p: string) => void;
    className: string;
}>) {
    const [filter, setFilter] = useState<FavoriteFilter>('all');
    const kinds = section.settings?.kinds;
    const ofKinds = useMemo(
        () => (kinds?.length ? favorites.items.filter(item => kinds.includes(favoriteKind(item))) : favorites.items),
        [favorites.items, kinds],
    );
    const count = countOf(section);
    const items = useMemo(() => filterFavorites(ofKinds, filter).slice(0, count), [ofKinds, filter, count]);
    return (
        <SectionFrame
            className={className}
            label="Your favorites"
            icon={faHeart}
            kicker="Your favorites"
            title="What you've saved"
            status={favorites.status}
            empty={ofKinds.length === 0}
            emptyText="Models and functionals you mark as a favorite will show up here."
            errorText="Couldn't load your favorites."
            onRetry={favorites.retry}
            skeleton={<SectionSkeleton variant={variantOf(section)}/>}
        >
            <FavoriteKindChips value={filter} onChange={setFilter}/>
            {items.length === 0 ? (
                <SectionMessage>No favorites of this kind yet.</SectionMessage>
            ) : (
                <ItemsView variant={variantOf(section)} items={items} navigate={navigate}
                           cover={favoriteCover} routeOf={favoriteRoute}/>
            )}
        </SectionFrame>
    );
}

/** What a failed write says, as a toast (a write made from a menu or a row, not a form). */
function toastOutcome(outcome: WriteOutcome) {
    if (!outcome.ok) toast.error(outcomeMessages(outcome).join(' '));
}

export default function DiscoverV1Editorial() {
    const navigate = useNavigate();
    const { info } = useAppInfo();
    const { userProfile } = useAuth();
    const search = useDiscoverSearch();
    const discover = useDiscoverLayout(userProfile?._id);
    const {layout, apply} = discover;
    // Only the lists the drawn sections need; none while the layout is not known yet.
    const needs = useMemo(() => (discover.holding ? NO_LISTS : listsFor(layout)), [discover.holding, layout]);
    const data = useDiscoverData(needs);
    const [customizing, setCustomizing] = useState(false);
    const customizeRef = useRef<HTMLButtonElement | null>(null);
    const closeCustomize = useCallback(() => {
        discover.flush();
        setCustomizing(false);
        customizeRef.current?.focus();
    }, [discover]);
    useEffect(() => () => {
        disposePreviewRenderer();
    }, []);
    const isSearching = search.active;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const problemsId = useId();
    const panelId = useId();
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelFocus, setPanelFocus] = useState<Field | null>(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const results = search.results;

    // Saving: the dialog's draft, and the saved search being edited (#2291).
    const [saveDraft, setSaveDraft] = useState<{ query: SearchQuery; asNew: boolean } | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const editing = searchOf(layout, editingId ?? undefined);
    // Editing ends when the search is cleared: the sections are back.
    const [wasSearching, setWasSearching] = useState(isSearching);
    if (wasSearching !== isSearching) {
        setWasSearching(isSearching);
        if (!isSearching && editingId) setEditingId(null);
    }

    const openPanel = useCallback((field: Field | null) => {
        setPanelFocus(field);
        setPanelOpen(true);
    }, []);
    const fillExample = (example: string) => {
        setHelpOpen(false);
        search.replace(`${example} `);
        requestAnimationFrame(() => inputRef.current?.focus());
    };
    const copyLink = () => {
        const link = searchLink(search.settled, userProfile?._id);
        if (!navigator.clipboard) { toast.error("Couldn't copy the link"); return; }
        navigator.clipboard.writeText(link).then(() => toast.success('Link copied'), () => toast.error("Couldn't copy the link"));
    };

    /** Put a saved search in the box: the results, the chips and the panel follow. */
    const loadSearch = (saved: SavedSearch) => search.replace(serializeQuery(fromModelQuery(saved.query), search.resolver.lookup));
    const editSearch = (saved: SavedSearch) => {
        loadSearch(saved);
        setEditingId(saved.id);
        openPanel(null);
        inputRef.current?.scrollIntoView?.({block: 'center', behavior: 'smooth'});
    };
    const runSearch = (saved: SavedSearch) => {
        setEditingId(null);
        loadSearch(saved);
    };
    const updateEdited = () => {
        if (!editing) return;
        const title = editing.title;
        void apply(updateSearchQuery(editing.id, toSavedQuery(search.settled))).then(outcome => {
            if (outcome.ok) {
                setEditingId(null);
                toast.success(`Updated “${title}”`);
            } else {
                toastOutcome(outcome);
            }
        });
    };
    const saveNew = (query: SearchQuery, values: SaveSearchValues) => {
        const change = addSearch({
            id: newSearchId(values.title, layout.searches.map(s => s.id)),
            title: values.title,
            description: values.description,
            query: toSavedQuery(query),
            show: values.show,
            place: values.place,
            variant: values.variant,
        });
        return apply(change).then(outcome => {
            if (outcome.ok) {
                setEditingId(null);
                toast.success(values.show ? `Saved “${values.title}” to Discover` : `Saved “${values.title}”`);
            }
            return outcome;
        });
    };

    const lastChip = search.chips.at(-1);
    const saveButton = editing ? null : (
        <button type="button" className="dsq-link" onClick={() => setSaveDraft({query: search.settled, asNew: false})}>
            ☆ Save search
        </button>
    );
    const editActions = editing && (
        <>
            <button type="button" className="dsq-link" onClick={updateEdited}>Update saved search</button>
            <button type="button" className="dsq-link" onClick={() => setSaveDraft({query: search.settled, asNew: true})}>
                Save as new
            </button>
        </>
    );
    const resultCount = results.results.length;
    const resultText = search.parsed.query.text.trim();
    const resultPlural = resultCount !== 1 ? 's' : '';
    const resultFor = resultText ? ` for "${resultText}"` : '';
    const sortNote = search.settled.sort ? ` · sorted by ${SORT_LABELS[search.settled.sort]}` : '';

    const {trending, picks, recent, favorites} = data;
    // A Custom page is told of a section type it has never had (#2292).
    const newTypes = layout.preset === 'custom' && missingTypes(layout).length > 0;
    const picksItemsAll = picks.items as unknown as DiscoverItem[];

    // The sections the layout draws, in its order (#2291).
    const drawn = useMemo(() => drawnSections(layout), [layout]);

    // The featured carousel: its source and count are the first featured section's.
    const featuredSection = drawn.find(s => s.type === 'featured');
    const featuredSource = featuredSection ? featuredSourceOf(featuredSection) : DEFAULT_FEATURED_SOURCE;
    const featuredCount = featuredSection ? countOf(featuredSection, layout) : 0;
    const featured = useMemo<DiscoverItem[]>(
        () => (featuredCount ? featuredItems(picksItemsAll, favorites.items, featuredSource, featuredCount) : []),
        [picksItemsAll, favorites.items, featuredSource, featuredCount],
    );
    const featuredIds = useMemo(() => new Set(featured.map(f => f._id)), [featured]);
    const featuredStatus = featuredStatusOf(featuredSource, picks.status, favorites.status);

    // Trending is shown minus the featured models, so it waits for them:
    // shown first, a featured model would sit in the shelf and then vanish
    // from it. Failed picks feature nothing, so the shelf need not wait.
    const trendingStatus: SectionStatus =
        featuredSection && trending.status === 'ready' && featuredStatus === 'loading' ? 'loading' : trending.status;

    const sectionFor = (section: DiscoverSection, index: number) => {
        const variant = variantOf(section, layout);
        const count = countOf(section, layout);
        const className = index === drawn.length - 1 ? 'mb-3' : 'mb-9';
        switch (section.type as DiscoverSectionType) {
            case 'featured':
                // ── Featured carousel ──
                if (featuredStatus === 'loading') return <HeroSkeleton key={section.id}/>;
                if (featuredStatus === 'error') {
                    return (
                        <section key={section.id} className="mb-9" aria-label="Featured">
                            <SectionMessage onRetry={() => { picks.retry(); if (featuredSource !== 'picks') favorites.retry(); }}>
                                Couldn't load the featured models.
                            </SectionMessage>
                        </section>
                    );
                }
                if (featured.length > 0) {
                    return <FeaturedCarousel key={section.id} items={featured.slice(0, count)} navigate={navigate}/>;
                }
                if (featuredSource === 'favorites') {
                    return (
                        <section key={section.id} className="mb-9" aria-label="Featured">
                            <SectionMessage>Models you mark as a favorite will be featured here.</SectionMessage>
                        </section>
                    );
                }
                return null;
            case 'trending': {
                const items = (trending.items as unknown as DiscoverItem[]).filter(x => !featuredIds.has(x._id)).slice(0, count);
                return (
                    <SectionFrame
                        key={section.id}
                        className={className}
                        label="Trending now"
                        icon={faFireFlameCurved}
                        kicker="Trending now"
                        title="What the community is running"
                        status={trendingStatus}
                        empty={items.length === 0}
                        emptyText="Nothing is trending yet."
                        errorText="Couldn't load trending models."
                        onRetry={trending.retry}
                        skeleton={<SectionSkeleton variant={variant}/>}
                    >
                        <ItemsView variant={variant} items={items} navigate={navigate}/>
                    </SectionFrame>
                );
            }
            case 'favorites':
                return <FavoritesSection key={section.id} section={section} favorites={favorites} navigate={navigate} className={className}/>;
            case 'editorsPicks': {
                // ── The picks the carousel does not feature ──
                // Never a model the carousel shows, whatever it draws from: with
                // picks or a mix, the grid goes on from the picks it left; from
                // favorites only, it starts at the first pick. It waits for the
                // carousel (as trending does), so a mix settling never reshuffles
                // it, and is not drawn when the carousel already shows every pick.
                if (featuredSection && featuredStatus === 'loading') return null;
                const items = picksItemsAll.filter(p => !featuredIds.has(p._id)).slice(0, count);
                return items.length > 0 && (
                    <section key={section.id} className={className} aria-label="Editor's picks">
                        <SectionHead icon={faStar} kicker="Editor's picks" title="Hand-curated, freshly polished"/>
                        <ItemsView variant={variant} items={items} navigate={navigate} picksGrid/>
                    </section>
                );
            }
            case 'continueEditing': {
                // ── Continue editing: models you saved or ran (#2208) ──
                const items = recent.items.slice(0, count);
                return (
                    <SectionFrame
                        key={section.id}
                        className={className}
                        label="Your recent activity"
                        icon={faClock}
                        kicker="Your recent activity"
                        title="Continue editing"
                        status={recent.status}
                        empty={items.length === 0}
                        emptyText="Models you save or run will show up here."
                        errorText="Couldn't load your recent activity."
                        onRetry={recent.retry}
                        skeleton={<SectionSkeleton variant={variant}/>}
                    >
                        <ItemsView variant={variant} items={items} navigate={navigate}/>
                    </SectionFrame>
                );
            }
            case 'scheduledTasks':
                // ── The viewer's own tasks and their last runs (#2303): loads on its own ──
                return <ScheduledTasksSection key={section.id} section={section} layout={layout}
                                              profileId={userProfile?._id} className={className}/>;
            case 'sharedWithMe':
                // ── Models shared with the viewer, latest updated first (#2303): loads on its own ──
                return <SharedWithMeSection key={section.id} section={section} layout={layout}
                                            navigate={navigate} className={className}/>;
            case 'savedSearch': {
                const saved = searchOf(layout, section.settings?.searchId)!;
                return (
                    <SavedSearchSection
                        key={section.id}
                        className={className}
                        section={section}
                        search={saved}
                        navigate={navigate}
                        onEdit={editSearch}
                        onMove={dir => { void apply(moveSection(section.id, dir)).then(toastOutcome); }}
                        canMoveUp={index > 0}
                        canMoveDown={index < drawn.length - 1}
                        filterBox={filterBoxOf(section, layout)}
                        filterBoxModes={filterBoxModesOf(layout)}
                        onFilterBox={mode => { void apply(setFilterBox(section.id, mode)).then(toastOutcome); }}
                        onRemove={() => {
                            void apply(removeSection(section.id)).then(outcome => {
                                if (outcome.ok) toast.success(`Removed “${saved.title}” from Discover. It is still in your saved searches.`);
                                else toastOutcome(outcome);
                            });
                        }}
                    />
                );
            }
            default:
                return null;
        }
    };

    // Create a brand-new workflow: mint an id, confirm it's unused, open the builder.
    const createNewWorkflow = async () => {
        const id = crypto.randomUUID();
        try {
            await getModel(id);
            return; // (astronomically unlikely) id already exists — bail
        } catch {
            /* 404 expected for a fresh id */
        }
        navigate(`/build/${id}`);
    };

    const renderResults = () => {
        if (search.searching) {
            return (
                <div style={{padding: '60px 0'}}>
                    <LoadingSpinner message="Searching..."/>
                </div>
            );
        }
        if (results.failed) {
            return <SectionMessage onRetry={results.retry}>Couldn't search. Check your connection and try again.</SectionMessage>;
        }
        if (results.problems) {
            return (
                <div className="edit-empty">
                    <span>This search can't run as it is: see the note under the search box.</span>
                    <button type="button" className="edit-section-link" onClick={() => setHelpOpen(true)}>Search help</button>
                </div>
            );
        }
        if (resultCount === 0) {
            return (
                <div className="edit-empty">
                    <span>{search.chips.length ? 'Nothing matched these filters.' : 'Nothing matched. Try different keywords.'}</span>
                    {search.chips.length > 0 && (
                        <SearchChips chips={search.chips} onEdit={openPanel} onRemove={search.removeChip}
                                     onClearAll={search.clearAll} className="justify-center"/>
                    )}
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                        {lastChip && (
                            <button type="button" className="edit-section-link" onClick={() => search.removeChip(lastChip)}>
                                Remove the last filter ({chipText(lastChip)})
                            </button>
                        )}
                        <button type="button" className="edit-section-link" onClick={() => setHelpOpen(true)}>
                            Search help
                        </button>
                    </div>
                </div>
            );
        }
        return <CoverGrid items={results.results} navigate={navigate}/>;
    };

    const renderMain = () => {
        if (isSearching) {
            return (
                <>
                    {editing && (
                        <output className="dsq-editing">
                            <span>Editing the saved search <strong>{editing.title}</strong></span>
                            <span className="flex flex-wrap items-center gap-2">
                                {editActions}
                                <button type="button" className="dsq-link" onClick={() => setEditingId(null)}>Stop editing</button>
                            </span>
                        </output>
                    )}
                    <div className="edit-results-head">
                        <p className="text-aurora-text-muted/70 text-[13px] font-semibold m-0" aria-live="polite">
                            {search.searching
                                ? 'Searching...'
                                : `${resultCount} result${resultPlural}${resultFor}${sortNote}`}
                        </p>
                        <div className="flex items-center gap-2">
                            {saveButton}
                            <button type="button" className="dsq-link" onClick={copyLink}
                                    title="Copy a link to this search">
                                <FontAwesomeIcon icon={faLink} className="mr-1.5 text-[10px]"/>
                                Copy link
                            </button>
                        </div>
                    </div>
                    {renderResults()}
                    {!search.searching && results.hasMore && resultCount > 0 && (
                        <div className="text-center mt-[18px]">
                            <button
                                onClick={results.loadMore}
                                style={{
                                    padding: '9px 22px',
                                    borderRadius: 999,
                                    background: 'rgb(var(--brew-blue-light) / 0.1)',
                                    border: '1px solid rgb(var(--brew-blue-light) / 0.22)',
                                    color: '#3cb4e5',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                }}
                            >
                                {results.loadingMore ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    )}
                </>
            );
        }
        // A first visit, waiting a moment for the layout rather than
        // drawing one it may replace (see DiscoverLayoutStore).
        if (discover.holding) return <div className="min-h-[380px]" aria-busy="true"/>;
        return drawn.map(sectionFor);
    };

    return (
        <>
            <style>{styles}</style>
            <AuroraBackground/>

            <div className={`min-h-[var(--app-content-height)] px-5 py-8 pb-32${customizing ? ' dcz-page-open' : ''}`}>
                <div style={{maxWidth: 1280, margin: '0 auto'}}>
                    <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
                        <div>
                            <p className="text-[10px] tracking-[0.16em] uppercase text-brew-blue-light/85 font-bold mb-1">
                                Brewlytics · Discover
                            </p>
                            <h1 className="text-3xl font-semibold tracking-tight"
                                style={{color: '#d4eaf5', fontSize: 30, lineHeight: 1.15}}>
                                <FontAwesomeIcon icon={faCompass}
                                                 className="mr-2 align-middle text-brew-blue-light/70 text-[26px]"/>
                                What's worth your time today
                            </h1>
                        </div>
                        <div className="flex items-start gap-3 flex-wrap justify-end w-full sm:w-auto">
                            <div className="relative flex items-start gap-2 w-full sm:w-[min(540px,62vw)]">
                                <div className="flex-1 min-w-0">
                                    <SearchBox
                                        text={search.text}
                                        tokens={search.parsed.tokens}
                                        caret={search.caret}
                                        onType={search.type}
                                        onReplace={search.replace}
                                        onSettle={search.settle}
                                        onCaret={search.setCaret}
                                        onFocusChange={search.setFocused}
                                        onLearn={search.resolver.learn}
                                        onOpenHelp={() => setHelpOpen(true)}
                                        inputRef={inputRef}
                                        describedBy={search.problems.length ? problemsId : undefined}
                                        invalid={search.problems.length > 0}
                                    />
                                    {search.problems.length > 0 && (
                                        <ul id={problemsId} className="dsq-problems" aria-live="polite">
                                            {search.problems.map(p => <li key={p}>{p}</li>)}
                                        </ul>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="dsq-filters-btn"
                                    aria-expanded={panelOpen}
                                    aria-controls={panelOpen ? panelId : undefined}
                                    aria-label={search.chips.length ? `Filters, ${search.chips.length} active` : 'Filters'}
                                    onClick={() => (panelOpen ? setPanelOpen(false) : openPanel(null))}
                                >
                                    <FontAwesomeIcon icon={faSliders}/>
                                    <span className="hidden sm:inline">Filters</span>
                                    {search.chips.length > 0 && <span className="dsq-badge">{search.chips.length}</span>}
                                </button>
                                {panelOpen && (
                                    <FiltersPanel
                                        id={panelId}
                                        query={search.parsed.query}
                                        lookup={search.resolver.lookup}
                                        onSet={search.setFieldValue}
                                        onLearn={search.resolver.learn}
                                        onClearAll={search.clearAll}
                                        onClose={() => setPanelOpen(false)}
                                        focusField={panelFocus}
                                        count={{n: resultCount, more: results.hasMore, searching: search.searching}}
                                        sheetBottom={info?.capco?.enabled ? CLASSIFICATION_BANNER_HEIGHT : 0}
                                        saveSearch={isSearching ? (editActions || saveButton) : null}
                                        saved={(
                                            <SavedSearchList
                                                layout={layout}
                                                apply={apply}
                                                onRun={runSearch}
                                                currentId={editing?.id ?? null}
                                                onError={messages => toast.error(messages.join(' '))}
                                            />
                                        )}
                                    />
                                )}
                            </div>
                            <button
                                ref={customizeRef}
                                type="button"
                                className="dsq-filters-btn"
                                aria-expanded={customizing}
                                aria-label={newTypes ? 'Customize, a new section is available' : 'Customize'}
                                onClick={() => (customizing ? closeCustomize() : setCustomizing(true))}
                            >
                                <FontAwesomeIcon icon={faTableLayout}/>
                                <span className="hidden sm:inline">Customize</span>
                                {newTypes && <span className="dcz-new-dot" aria-hidden="true"/>}
                            </button>
                            <button className="edit-new-btn" onClick={createNewWorkflow} title="Create a new workflow">
                                <FontAwesomeIcon icon={faDiagramProject} className="text-sm"/>
                                New Workflow
                            </button>
                        </div>
                    </div>

                    <SearchChips
                        className="mb-5 -mt-2"
                        chips={search.chips}
                        onEdit={openPanel}
                        onRemove={search.removeChip}
                        onClearAll={search.clearAll}
                    />
                    <SearchHelp open={helpOpen} onClose={() => setHelpOpen(false)} onExample={fillExample}/>
                    {saveDraft && (
                        <SaveSearchDialog
                            initialTitle={summarizeQuery(saveDraft.query, search.resolver.lookup)}
                            asNew={saveDraft.asNew}
                            onSave={values => saveNew(saveDraft.query, values)}
                            onClose={() => setSaveDraft(null)}
                        />
                    )}

                    {renderMain()}
                </div>
            </div>
            {customizing && (
                <Suspense fallback={null}>
                    <CustomizeSheet
                        layout={layout}
                        saving={discover.saving}
                        apply={apply}
                        reset={discover.reset}
                        onClose={closeCustomize}
                    />
                </Suspense>
            )}
            <AppFooter
              closeIcon={<FontAwesomeIcon icon={faXmark}/>}
              bottomOffset={info?.capco?.enabled ? CLASSIFICATION_BANNER_HEIGHT : 0}
            />
        </>
    );
}
